// Per-document view of the per-chunk `sources` event.
//
// The server emits one entry per passage (so a document can appear several
// times) plus, with the tiered layout or an agent turn, one `detail: 'summary'`
// entry per document the model only saw as a summary card.
import type { SourceDoc } from '../types';

export const isSummarySource = (s: SourceDoc): boolean => s.detail === 'summary';

export interface SourceDocGroup {
  doc: SourceDoc;        // first full-text entry, else the summary entry
  summaryOnly: boolean;  // never read in full
  passages: number;      // full-text entries for this document
}

export function groupSourceDocs(sources: SourceDoc[]): SourceDocGroup[] {
  const byKey = new Map<string, SourceDocGroup>();
  for (const s of sources) {
    const full = !isSummarySource(s);
    const g = byKey.get(s.cite_key);
    if (!g) {
      byKey.set(s.cite_key, { doc: s, summaryOnly: !full, passages: full ? 1 : 0 });
    } else if (full) {
      if (g.summaryOnly) g.doc = s;
      g.summaryOnly = false;
      g.passages += 1;
    }
  }
  return [...byKey.values()];
}

/** Full-text document, summary-only document and passage counts. */
export function countSourceDocs(sources: SourceDoc[]): { full: number; summary: number; passages: number } {
  const groups = groupSourceDocs(sources);
  const summary = groups.filter((g) => g.summaryOnly).length;
  return {
    full: groups.length - summary,
    summary,
    passages: groups.reduce((n, g) => n + g.passages, 0),
  };
}
